import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { Flex } from "../../../components/Flex";
import { Input } from "../../../components/Input";
import { Entry } from "../../../model/entry/Entry";
import { InputConfigDate } from "../../../model/form/dto/InputDateConfigDto";
import { InputTypes } from "../../../model/form/types/InputTypes.enum";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import { entrySliceSelectors } from "../../../redux/entry/entry.selector";
import { useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { standardDate } from "../../../utils/Date";
import { isCustomDateInput } from "../../../utils/isCustomDateInput";
import { Checkbox, Tooltip } from "../../../utils/local-antd";
import { useDrawersContext } from "../../tasks/drawers-context";

const Container = styled(Flex)<{ active?: boolean }>`
  position: relative;
  align-items: center;

  input,
  .ant-picker {
    border-color: transparent !important;
    transition: all 0.2s ease-in-out !important;
  }

  ${({ active, theme }) =>
    active
      ? `
    input,
    .ant-picker {
      border-color: ${theme.darkSub} !important;
    }
  `
      : `
    input:hover,
    .ant-picker:hover {
      border-color: ${theme.darkSub} !important;
    }
  `}

  .ant-picker-suffix {
    display: none !important;
  }

  .ant-picker-input input::placeholder {
    opacity: 0;
  }

  .ant-picker:focus-within {
    box-shadow: none !important;
  }

  .ant-picker-disabled {
    background-color: transparent !important;
  }
`;

const StyledInput = styled(Input)`
  && {
    padding: 0 !important;
    padding-inline-start: ${Spacing.xs} !important;
  }
`;

const BookingValue = styled.button`
  width: 100%;
  padding-block: ${Spacing.xs};
  padding-inline: ${Spacing.xs};
  background: transparent;
  border: 1px solid transparent;
  border-radius: ${Rounded.md};
  color: inherit;
  font: inherit;
  text-align: start;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  transition: border-color 0.2s ease-in-out;

  &:hover {
    border-color: ${({ theme }) => theme.darkSub};
  }
`;

type CustomFieldEditableCellProps = {
  entry: Partial<Entry>;
  field: string;
  type?: InputTypes;
  dateInputConfig?: InputConfigDate;
  /** Resolving to `false` means the save failed, and the cell shows the stored value again. */
  onUpdate: (entry: Partial<Entry>) => void | Promise<boolean | void>;
  active?: boolean;
  disableCountrySelect?: boolean;
  /** Saves every change as it is made, instead of when the cell is left. */
  commitOnChange?: boolean;
};

/**
 * A custom field's cell, edited in place. There's no save button: a text value
 * is saved as it's left (blur or Enter, Escape puts the old value back) and a
 * date as soon as one is picked. With `commitOnChange` a text value is saved
 * on every keystroke instead.
 */
export const CustomFieldEditableCell: React.FC<
  CustomFieldEditableCellProps
> = ({
  entry,
  field,
  type,
  dateInputConfig,
  onUpdate,
  disableCountrySelect,
  active,
  commitOnChange,
}) => {
  const value = entry.fields?.[field] ?? "";

  const [localValue, setLocalValue] = useState<string | Date>(value);
  const [dirty, setDirty] = useState(false);
  // Escape blurs the input to leave it, and that blur must not save the
  // edit it was meant to throw away.
  const discardOnBlur = useRef(false);

  const loadingId = useAppSelector(
    entrySliceSelectors.selectCustomEntryLoadingId,
  );
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const { setEntry, setEntryUpdateVisible } = useDrawersContext();

  const isDateInput = type?.toLowerCase() === InputTypes.DATE.toLowerCase();
  const isNumberInput = type?.toLowerCase() === InputTypes.NUMBER.toLowerCase();
  const isCheckboxInput =
    type?.toLowerCase() === InputTypes.CHECKBOX.toLowerCase();
  const isBookingDateInput = isCustomDateInput(type, dateInputConfig);
  // `localValue` is a string end to end here - fresh from the API on load,
  // and still a (naive, no-offset) string after editing since
  // AtiraDatePicker's onChange never produces an actual Date instance. The
  // old `instanceof Date` check was therefore never true, so this always
  // fell through to the raw, unformatted value.
  const displayValue: string = isDateInput
    ? standardDate(
        localValue,
        user,
        isBookingDateInput,
        false,
        isBookingDateInput ? dateInputConfig?.timezone : undefined,
      )
    : String(localValue);

  const save = async (newValue: string | Date) => {
    setDirty(false);

    const saved = await onUpdate({
      ...entry,
      fields: { ...entry.fields, [field]: newValue.toString() },
    });

    if (saved === false) {
      setLocalValue(value);
    }
  };

  const onBlur = () => {
    if (discardOnBlur.current) {
      discardOnBlur.current = false;
      return;
    }

    if (dirty) {
      save(localValue);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      // Saving is the blur's job, so the two ways out can't both save
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      discardOnBlur.current = true;
      setLocalValue(value);
      setDirty(false);
      e.currentTarget.blur();
    }
  };

  useEffect(() => {
    setLocalValue(value);
    setDirty(false);
  }, [value]);

  // A booking needs the slot grid and booked-slot greying, which only the
  // Update Entry drawer has - a free date-time picker here could pick times
  // that aren't real slots.
  if (isBookingDateInput) {
    return (
      <Container active={active}>
        <Tooltip title={displayValue}>
          <BookingValue
            type="button"
            onClick={() => {
              setEntry(entry as Entry);
              setEntryUpdateVisible(true);
            }}
          >
            {displayValue}
          </BookingValue>
        </Tooltip>
      </Container>
    );
  }

  if (isCheckboxInput) {
    return (
      <Container active={active}>
        <Checkbox
          checked={String(value) === "true"}
          disabled={loadingId === entry._id}
          onChange={(e) => save(String(e.target.checked))}
        />
      </Container>
    );
  }

  // With `commitOnChange` there is nothing left to save when the cell is left
  const saveOnLeave = !isDateInput && !commitOnChange;

  const input = (
    <StyledInput
      // The picker needs a raw, machine-parseable value to read/edit -
      // `displayValue` is a human-formatted string (e.g. "2026-09-15 -
      // 13:00") that dayjs can't reliably parse, which showed as
      // "Invalid Date" once fed back in here. Only the Tooltip around it
      // shows the formatted text.
      value={isDateInput ? localValue : displayValue}
      onChange={
        isDateInput
          ? undefined
          : (e) => {
              const newValue = isNumberInput
                ? e.target.value.replace(/\D/g, "").slice(0, Lengths.NUMBER)
                : e.target.value;

              setLocalValue(newValue);

              if (commitOnChange) {
                save(newValue);
              } else {
                setDirty(true);
              }
            }
      }
      inputMode={isNumberInput ? "numeric" : undefined}
      onBlur={saveOnLeave ? onBlur : undefined}
      onKeyDown={saveOnLeave ? onKeyDown : undefined}
      onChangeDate={
        isDateInput
          ? (date) => {
              // The picker's clear control (or an unparseable manual
              // edit) reports `null` - `localValue` is typed
              // `string | Date` and `save` always calls `.toString()` on
              // it, which throws on `null` instead of saving the field
              // as empty.
              const picked = date ?? "";

              setLocalValue(picked);
              save(picked);
            }
          : undefined
      }
      disabled={loadingId === entry._id}
      type={isDateInput ? "date" : "text"}
    />
  );

  return (
    <Container active={active}>
      {commitOnChange ? (
        input
      ) : (
        <Tooltip title={displayValue}>
          {/* Tooltip needs an element to hang on to - Input is a component that
              can't be handed the ref and the pointer events it wants */}
          <span style={{ display: "block", width: "100%" }}>{input}</span>
        </Tooltip>
      )}
    </Container>
  );
};
